import { useEffect, useState, useCallback, useRef } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { ThemeProvider } from 'foundations-theming/components/ThemeProvider';
import { Text } from 'foundations-components/components/Text';
import { isUngatedToCHIRPEndpoints } from 'documents-ui/utils/gates';
import { useGetGdprWrapQuery } from '../../../__generated__/chirp/com/hubspot/sales/documents/rpc/SalesDocumentsGDPRService.hooks';
import CreateLinkSetup from './CreateLinkSetup';
import ShareShortlink from './ShareShortlink';
import * as DocumentsApi from 'documents-ui/api/DocumentsApi';
import * as DocumentsSettingsApi from 'documents-ui/api/DocumentsSettingsApi';
import { trackInteraction } from 'documents-lib/js/tracking/tracker';
import { addAlert } from 'documents-lib/js/utils/addAlert';
import { CLICK_SHARE_CREATE_LINK, CLICK_SHARE_ENTER_EMAIL, CLICK_SHARE_REQUIRE_EMAIL } from 'documents-lib/js/tracking/actions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function CreateLinkModal({
  gdprSettings,
  deckId,
  name,
  onReject
}) {
  const [shortLink, setShortLink] = useState(null);
  const [sharedWithVid, setSharedWithVid] = useState(null);
  const [email, setEmail] = useState('');
  const [requireEmail, setRequireEmail] = useState(true);
  const [isGettingLink, setIsGettingLink] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const isUngatedToChirp = isUngatedToCHIRPEndpoints();
  const [gdprSettingsState, setGdprSettingsState] = useState(gdprSettings);
  const hasTrackedEmailEntry = useRef(false);
  useEffect(() => {
    if (!gdprSettingsState) {
      DocumentsSettingsApi.fetchGDPRSettings(deckId).then(_gdprSettings => setGdprSettingsState(_gdprSettings)).catch(error => {
        // TODO: consider showing an error message in modal
        // instead of infinite loading spinner
        console.error(error);
      });
    }
  }, [deckId, gdprSettingsState]);
  const handleShareSuccess = useCallback(shareData => {
    setIsGettingLink(false);
    setShortLink(shareData.get('shortLink'));
    setSharedWithVid(shareData.get('sharedWithVid'));
  }, []);
  const handleShareSuccessCHIRP = useCallback(shareData => {
    var _shareData$shortLink, _shareData$sharedWith;
    setIsGettingLink(false);
    setShortLink((_shareData$shortLink = shareData.shortLink) !== null && _shareData$shortLink !== void 0 ? _shareData$shortLink : null);
    setSharedWithVid((_shareData$sharedWith = shareData.sharedWithViewerId) !== null && _shareData$sharedWith !== void 0 ? _shareData$sharedWith : null);
  }, []);
  const {
    data: gdprSettingsCHIRP,
    loading: gdprSettingsCHIRPLoading
  } = useGetGdprWrapQuery({
    variables: {
      documentId: deckId
    },
    skip: !deckId || !isUngatedToChirp
  });
  const handleShareFailure = useCallback(() => {
    addAlert('createShareableLink', 'danger', {
      name
    });
    setIsGettingLink(false);
  }, [name]);
  const handleEmailChange = useCallback(value => {
    setEmail(value);
    if (!hasTrackedEmailEntry.current && value.trim() !== '') {
      trackInteraction(CLICK_SHARE_ENTER_EMAIL);
      hasTrackedEmailEntry.current = true;
    }
  }, [hasTrackedEmailEntry]);
  const handleRequireEmailChange = useCallback(value => {
    setRequireEmail(value);
    trackInteraction(CLICK_SHARE_REQUIRE_EMAIL);
  }, []);
  const handleEmailValidationChange = useCallback(value => {
    setIsValidated(value);
  }, []);
  const handleClickCreateLink = useCallback(() => {
    trackInteraction(CLICK_SHARE_CREATE_LINK);
    setIsGettingLink(true);
    const sharePromise = isUngatedToChirp ? DocumentsApi.shareDocumentCHIRP({
      id: deckId,
      name,
      email,
      requireEmail
    }) : DocumentsApi.shareDocument({
      id: deckId,
      email,
      requireEmail
    });
    const handleShareSuccessCallback = isUngatedToChirp ? handleShareSuccessCHIRP : handleShareSuccess;
    sharePromise.then(handleShareSuccessCallback, handleShareFailure).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }, [deckId, email, handleShareSuccess, handleShareSuccessCHIRP, handleShareFailure, requireEmail, isUngatedToChirp, name]);
  const isGdprSettingsLoading = isUngatedToChirp ? gdprSettingsCHIRPLoading : !gdprSettingsState;
  const legalConsentEnabled = isUngatedToChirp ? gdprSettingsCHIRP === null || gdprSettingsCHIRP === void 0 ? void 0 : gdprSettingsCHIRP.legalConsentEnabled : gdprSettingsState === null || gdprSettingsState === void 0 ? void 0 : gdprSettingsState.legalConsentEnabled;
  const renderBody = () => {
    if (isGettingLink || isGdprSettingsLoading) {
      return /*#__PURE__*/_jsx(TFlex, {
        children: /*#__PURE__*/_jsx(LoadingSpinner, {
          grow: true,
          minHeight: 200,
          layout: "centered",
          "data-loading": true
        })
      });
    }
    if (shortLink && sharedWithVid) {
      return /*#__PURE__*/_jsx(ShareShortlink, {
        email: email,
        name: name,
        shortLink: shortLink,
        sharedWithVid: sharedWithVid
      });
    }
    return /*#__PURE__*/_jsx(CreateLinkSetup, {
      deckId: deckId,
      email: email,
      legalConsentEnabled: legalConsentEnabled || false,
      isValidated: isValidated && email !== '',
      requireEmail: requireEmail,
      onEmailValidation: handleEmailValidationChange,
      onEmailChange: handleEmailChange,
      onRequireEmailChange: handleRequireEmailChange,
      onCreateLink: handleClickCreateLink
    });
  };
  return /*#__PURE__*/_jsx(Modal, {
    size: "auto",
    width: 480,
    "data-test-id": "create-link-modal",
    children: /*#__PURE__*/_jsxs(ThemeProvider, {
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: onReject
        }), /*#__PURE__*/_jsx(Text, {
          as: "h2",
          color: "text-core-on-fill-default",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "createLink.title"
          })
        })]
      }), /*#__PURE__*/_jsx(DialogBody, {
        paddingBlockEnd: "800",
        children: renderBody()
      })]
    })
  });
}
export default CreateLinkModal;